if (!customElements.get('outdoor-variant-content')) {
  customElements.define(
    'outdoor-variant-content',
    class OutdoorVariantContent extends HTMLElement {
      connectedCallback() {
        if (this.dataset.selectedRecord === 'true') {
          this.renderSetTechnicalData();
          this.selectionEvents = new AbortController();
          this.applySelectedRecord(JSON.parse(this.querySelector('[data-oe-carrez-selected]')?.textContent || 'null'));
          document.addEventListener('oe:carrez-selection', ({ detail }) => {
            if (String(detail.productId) !== this.dataset.productId) return;
            this.setAttribute('aria-busy', String(detail.status === 'loading'));
            if (detail.status === 'ready') this.applySelectedRecord(detail.record);
            else this.clearSelectedRecord(detail.status);
          }, { signal: this.selectionEvents.signal });
          return;
        }
        const dataElement = this.querySelector('[data-outdoor-variants]');

        try {
          this.variants = JSON.parse(dataElement?.textContent || '{}');
        } catch (error) {
          console.error('Outdoor variant data could not be parsed.', error);
          this.variants = {};
        }

        this.renderSetTechnicalData();
        this.renderVariant(this.dataset.initialVariantId);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const variantId = event?.data?.variant?.id;
            if (variantId) this.renderVariant(variantId);
          });
        }
      }

      disconnectedCallback() {
        this.selectionEvents?.abort();
        if (typeof this.unsubscribeVariantChange === 'function') this.unsubscribeVariantChange();
      }

      clearSelectedRecord(status) {
        delete this.dataset.currentVariantId;
        this.querySelectorAll('[data-outdoor-sku-technical-row]').forEach(row => row.remove());
        this.querySelectorAll('[data-outdoor-field]').forEach(element => { element.textContent = ''; });
        const message = this.querySelector('[data-outdoor-load-status]');
        if (message) message.textContent = status === 'loading' ? 'Variantendaten werden geladen.' : status === 'error' ? 'Variantendaten konnten nicht geladen werden. Bitte Auswahl erneut treffen.' : 'Diese Kombination ist nicht verfügbar.';
      }

      applySelectedRecord(record) {
        if (!record || String(record.productId) !== this.dataset.productId) { this.clearSelectedRecord('unavailable'); return; }
        const color = window.OeCarrezColors?.find(record.color);
        const values = { ...record, color: record.color_code ? record.color : color?.label || record.color, color_code: record.color_code || color?.code || '', ...this.technicalColor(record, color) };
        if (record.technical || record.top_edge) {
          let technical = {};
          try { technical = JSON.parse(record.technical || record.top_edge || '{}'); } catch { /* Keep unverified data empty. */ }
          values.top_edge = technical.topEdge || '';
          this.querySelectorAll('[data-outdoor-sku-technical-row]').forEach(row => row.remove());
          const grid = this.querySelector('[data-outdoor-set-technical-grid]');
          if (grid && Array.isArray(technical.technicalRows)) {
            technical.technicalRows.forEach(({ label, value }) => {
              if (!label || !value) return;
              const row = document.createElement('div');
              row.setAttribute('data-outdoor-sku-technical-row', '');
              const term = document.createElement('dt');
              const detail = document.createElement('dd');
              term.textContent = label;
              detail.textContent = value;
              row.append(term, detail);
              grid.append(row);
            });
          }
        }
        this.variants = { [String(record.id)]: values };
        this.renderVariant(record.id);
        this.dataset.currentVariantId = record.id;
        const message = this.querySelector('[data-outdoor-load-status]');
        if (message) message.textContent = '';
      }

      technicalColor(record, known) {
        const normalize = (value) => String(value || '').replace(/\s+/gu, ' ').trim().toLowerCase();
        const raw = String(record.color || '').trim();
        const stone = raw.match(/^(.+?)\s+\((STONE\s+\d+)\)$/u);
        if (stone && normalize(stone[2]) === normalize(record.color_code)) return { color: stone[1], color_code: stone[2], surface: record.surface || '' };
        const coded = raw.match(/^(.+?)\s+\(([^()]+)\)\s*-\s*(.+)$/u);
        if (coded && normalize(coded[1]) === normalize(record.color_code) && normalize(coded[3]) === normalize(record.surface)) {
          return { color: coded[2], color_code: coded[1], surface: record.surface };
        }
        if (!known || normalize(known.raw) !== normalize(raw)) return {};
        const parts = raw.match(/^(.+?)\s+\(([^()]+)\)\s*-\s*(.+)$/u);
        if (parts && normalize(parts[3]) === normalize(record.surface)) {
          if (/^RAL \d{4}$/u.test(parts[1]) && normalize(parts[1]) === normalize(record.color_code || known.code)) {
            return { color: parts[2], color_code: parts[1], surface: record.surface };
          }
          if (!/^RAL\b/iu.test(parts[1])) return { color: parts[1], color_code: parts[2], surface: record.surface };
        }
        const plainRal = raw.match(/^(RAL \d{4})\s+\(([^()]+)\)$/u);
        if (plainRal && normalize(plainRal[1]) === normalize(record.color_code || known.code)) {
          return { color: plainRal[2], color_code: plainRal[1], surface: record.surface || '' };
        }
        const repeated = raw.match(/^(.+?)\s+\(([^()]+)\)$/u);
        if (repeated && /^(?:A\d+[A-Z]*|OTS \d+)$/u.test(repeated[1]) && normalize(repeated[1]) === normalize(record.color_code || known.code)) {
          return { color: repeated[2], color_code: repeated[1], surface: record.surface || '' };
        }
        return repeated && normalize(repeated[1]) === normalize(repeated[2]) ? { color: repeated[1] } : {};
      }

      renderSetTechnicalData() {
        const source = this.querySelector('[data-outdoor-set-technical-data] .metafield-rich_text_field');
        const target = this.querySelector('[data-outdoor-set-technical-grid]');
        if (!source || !target || target.dataset.outdoorSetTechnicalGrid === 'ready') return;

        const existingFirstRow = target.firstElementChild;
        const rows = [];
        const normalizedLines = (element) => element.innerText
          .split(/\r?\n/u)
          .map((line) => line.replace(/\s+/gu, ' ').trim())
          .filter(Boolean);
        const addRow = (label, value, group = false) => {
          const row = document.createElement('div');
          if (group) row.classList.add('outdoor-variant__spec-group');
          const term = document.createElement('dt');
          const detail = document.createElement('dd');
          term.textContent = label.replace(/:\s*$/u, '').trim();
          detail.textContent = value.trim();
          row.append(term, detail);
          rows.push(row);
        };
        const addSpecification = (line) => {
          const separator = line.indexOf(':');
          if (separator > 0) addRow(line.slice(0, separator), line.slice(separator + 1));
          else addRow('Merkmal', line);
        };

        const children = Array.from(source.children);
        children.forEach((element, index) => {
          if (element.matches('ul, ol')) {
            element.querySelectorAll(':scope > li').forEach((item) => normalizedLines(item).forEach(addSpecification));
            return;
          }
          const lines = normalizedLines(element);
          if (!lines.length) return;
          if (lines.length > 1) {
            lines.forEach(addSpecification);
            return;
          }
          const next = children[index + 1];
          const isGroup = Boolean(next) && (next.matches('ul, ol') || !lines[0].includes(':'));
          if (isGroup) addRow(lines[0], '', true);
          else addSpecification(lines[0]);
        });

        if (!rows.length) return;
        rows.forEach((row) => target.insertBefore(row, existingFirstRow));
        target.dataset.outdoorSetTechnicalGrid = 'ready';
        source.closest('[data-outdoor-set-technical-data]').hidden = true;
      }

      renderVariant(variantId) {
        const variant = this.variants[String(variantId)];
        if (!variant) return;

        Object.entries(variant).forEach(([field, value]) => {
          this.querySelectorAll(`[data-outdoor-field="${field}"]`).forEach((element) => {
            const hasValue = value !== null && value !== undefined && String(value).trim() !== '';
            element.textContent = hasValue ? value : '';
            const row = element.closest('[data-outdoor-spec-row]');
            if (row) {
              const normalize = (text) => String(text || '').replace(/\s+/gu, '').toLowerCase();
              const commonLabel = { thickness: 'Materialstärke', material: 'Material', surface: 'Oberfläche', execution: 'Ausführung' }[field];
              const repeatedTechnicalValue = commonLabel && this.dataset.commonThickness &&
                (field !== 'thickness' || normalize(value) === normalize(this.dataset.commonThickness)) &&
                Array.from(this.querySelectorAll('[data-outdoor-set-technical-grid] > div:not([data-outdoor-spec-row])')).some(common =>
                  common.querySelector('dt')?.textContent.trim() === commonLabel && normalize(common.querySelector('dd')?.textContent) === normalize(value));
              row.hidden = !hasValue || Boolean(repeatedTechnicalValue);
              if (commonLabel && this.dataset.commonThickness) row.style.display = repeatedTechnicalValue ? 'none' : '';
              if (this.dataset.adezzSeries) row.style.display = !hasValue || repeatedTechnicalValue ? 'none' : '';
            }
          });
        });

        document.querySelectorAll('[data-outdoor-sku-value]').forEach((element) => {
          element.textContent = variant.sku || '—';
        });
      }

    }
  );
}
